import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import BrandLogo from "@/components/BrandLogo";

const NAV_LINKS = [
  { label: "Product", href: "/product" },
  { label: "AI Voice Agents", href: "/ai-voice-agents" },
  { label: "AI Marketing", href: "/ai-marketing" },
  { label: "CRM", href: "/crm" },
  { label: "Pricing", href: "/#pricing" },
  { label: "FAQ", href: "/#faq" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const getStartedHref = user ? "/dashboard" : "/signup";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-neutral-200/80 bg-white/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
        <a
          href="/"
          data-testid="brand-logo"
          className="flex items-center gap-2.5"
        >
          <BrandLogo />
          <span className="text-lg font-bold tracking-tight">Savy Kerno</span>
        </a>

        <nav
          data-testid="desktop-nav"
          className="hidden items-center gap-1 lg:flex"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-full px-3.5 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-[#111111]"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2.5 lg:flex">
          {!user && (
            <a
              href="/login"
              data-testid="sign-in-button"
              className="rounded-full px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
            >
              Sign In
            </a>
          )}
          <a
            href={getStartedHref}
            data-testid="header-get-started-button"
            className="rounded-full bg-[#111111] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-neutral-800 hover:shadow-md"
          >
            {user ? "Dashboard" : "Get Started"}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 text-[#111111] transition-colors hover:bg-neutral-100 lg:hidden"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-neutral-200 bg-white px-5 pb-6 pt-3 lg:hidden"
        >
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className="rounded-xl px-4 py-3 text-base font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="mt-4 flex flex-col gap-2.5">
            {!user && (
              <a
                href="/login"
                data-testid="mobile-sign-in-button"
                onClick={() => setOpen(false)}
                className="rounded-full border border-neutral-300 px-5 py-3 text-center text-sm font-semibold text-[#111111]"
              >
                Sign In
              </a>
            )}
            <a
              href={getStartedHref}
              data-testid="mobile-get-started-button"
              onClick={() => setOpen(false)}
              className="rounded-full bg-[#111111] px-5 py-3 text-center text-sm font-semibold text-white"
            >
              {user ? "Dashboard" : "Get Started"}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
